import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { SiteSettingsDto } from '@portfolio/shared';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/Feedback';
import { useProjects, useSite } from '../hooks';
import { ProjectCard } from '../ProjectCard';
import { Reveal } from '../Reveal';
import { useLocale } from '../i18n';

export function CategoryFilter({
  categories,
  labels,
  value,
  onChange,
}: {
  categories: string[];
  /** Display name per category (the value stays the English name) */
  labels?: Record<string, string>;
  value: string | undefined;
  onChange: (v: string | undefined) => void;
}) {
  const { t } = useLocale();
  if (categories.length < 2) return null;
  const all = [undefined, ...categories];
  return (
    <div role="group" aria-label={t.work.filterLabel} className="flex flex-wrap gap-2">
      {all.map((c) => (
        <button
          key={c ?? 'all'}
          type="button"
          aria-pressed={value === c}
          onClick={() => onChange(c)}
          className={cn(
            'h-10 rounded-full border px-4 text-ui transition-colors',
            value === c
              ? 'border-ink bg-ink text-canvas'
              : 'border-line text-graphite hover:border-ink hover:text-ink',
          )}
        >
          {c ? (labels?.[c] ?? c) : t.work.all}
        </button>
      ))}
    </div>
  );
}

/** "Selected work. Visual explorations." → one sentence per line, as in the design. */
const sentenceLines = (text: string) => text.split(/(?<=[.!?؟])\s+/).filter(Boolean);

export function Work({ s }: { s: SiteSettingsDto }) {
  // Which projects, in what order and how many: Admin → Projects → Homepage.
  const { data, isLoading, isError } = useProjects({ home: true });
  const { data: site } = useSite();
  const { t, to } = useLocale();
  const projects = data ?? [];
  const hasMore = (site?.projectCount ?? 0) > projects.length;

  return (
    <section id="work" aria-labelledby="work-title" className="container-site pb-16 md:pb-[92px]">
      <div aria-hidden className="h-px bg-line" />
      <Reveal className="flex flex-col gap-5 pt-12 md:flex-row md:items-start md:justify-between md:gap-10 md:pt-[47px]">
        <h2 id="work-title" className="text-h2 font-bold text-strong">
          {sentenceLines(s.workHeading).map((line) => (
            <span key={line} className="block">
              {line}
            </span>
          ))}
        </h2>
        {s.workIntro && (
          <p className="max-w-[300px] text-body text-graphite md:mt-[2px] md:text-justify">
            {s.workIntro}
          </p>
        )}
      </Reveal>

      {isLoading ? (
        <div className="mt-10 grid gap-6 md:mt-[73px] md:grid-cols-2 md:gap-x-[33px] md:gap-y-6">
          {Array.from({ length: Math.min(s.homeProjectsCount, 4) }, (_, i) => (
            <Skeleton key={i} className="aspect-[546/522] rounded-[40px]" />
          ))}
        </div>
      ) : isError ? (
        <p className="mt-12 text-muted">{t.work.loadError}</p>
      ) : projects.length === 0 ? (
        <p className="mt-12 text-body text-muted">{t.work.empty}</p>
      ) : (
        <ul className="mt-10 grid gap-6 md:mt-[73px] md:grid-cols-2 md:gap-x-[33px] md:gap-y-6">
          {projects.map((p, i) => (
            <Reveal as="li" key={p.id} delay={(i % 2) * 0.08}>
              <ProjectCard project={p} />
            </Reveal>
          ))}
        </ul>
      )}

      {hasMore && (
        <div className="mt-10 flex justify-center">
          <Link
            to={to('/work')}
            className="group inline-flex h-12 items-center gap-2 rounded-lg border border-line px-6 text-ui text-graphite transition-colors hover:border-ink hover:text-ink"
          >
            {t.work.viewAll}
            <ArrowRight className="size-4 transition-transform group-hover:translate-x-1 rtl:-scale-x-100 rtl:group-hover:-translate-x-1" />
          </Link>
        </div>
      )}

      {(s.workNote || s.workNoteDetail) && (
        <p className="mt-12 text-center text-lead md:mt-[60px]">
          {s.workNote && <span className="block font-semibold text-graphite">{s.workNote}</span>}
          {s.workNoteDetail && (
            <span className="block font-light text-graphite">{s.workNoteDetail}</span>
          )}
        </p>
      )}
    </section>
  );
}
